const cloneStocks = (stocks = []) => stocks.map((stock) => ({ ...stock }))

const buildEntry = (baseEntry, overrides = {}) => ({
  ...baseEntry,
  ...overrides,
  stocks: (() => {
    if (!overrides.stocks) return cloneStocks(baseEntry.stocks)

    const overrideStocks = cloneStocks(overrides.stocks)
    const overrideSymbols = new Set(overrideStocks.map((stock) => stock.symbol))
    const supplementalStocks = cloneStocks(baseEntry.stocks).filter((stock) => !overrideSymbols.has(stock.symbol))

    const supplementalWeight = supplementalStocks.reduce((sum, stock) => sum + (stock.weight ?? 0), 0)
    const overrideWeight = overrideStocks.reduce((sum, stock) => sum + (stock.weight ?? 0), 0)
    const targetOverrideWeight = Math.max(0, 100 - supplementalWeight)
    const multiplier = overrideWeight > 0 ? targetOverrideWeight / overrideWeight : 1

    const scaledOverrides = overrideStocks.map((stock) => ({
      ...stock,
      weight: Number(((stock.weight ?? 0) * multiplier).toFixed(2)),
    }))

    const scaledWeight = scaledOverrides.reduce((sum, stock) => sum + (stock.weight ?? 0), 0)
    const drift = Number((targetOverrideWeight - scaledWeight).toFixed(2))
    if (scaledOverrides.length && drift !== 0) {
      scaledOverrides[0].weight = Number((scaledOverrides[0].weight + drift).toFixed(2))
    }

    return [...scaledOverrides, ...supplementalStocks]
  })(),
})

const findEntryByName = (entries, displayName) => entries.find((entry) => entry.display_name === displayName)

export const createLiveDemoSteps = (competition) => {
  const nvidia = findEntryByName(competition.entries, 'NvidiaOrNothing')
  const steady = findEntryByName(competition.entries, 'GoldenHandsGracie')
  const momentum = findEntryByName(competition.entries, 'MomentumMike')
  const lastPlaceBase = competition.entries[competition.entries.length - 1]

  return [
    {
      topThree: [
        buildEntry(nvidia, { rank: 1, position_change: nvidia.position_change, total_return: 21.3 }),
        buildEntry(steady, { rank: 2, position_change: steady.position_change, total_return: 20.9 }),
        buildEntry(momentum, { rank: 3, position_change: momentum.position_change, total_return: 20.4 }),
      ],
      lastEntry: buildEntry(lastPlaceBase, { position_change: lastPlaceBase.position_change, total_return: -8.4 }),
    },
    {
      topThree: [
        buildEntry(nvidia, {
          rank: 1,
          position_change: 0,
          total_return: 21.4,
          stocks: [
            { symbol: 'NVDA', weight: 46, return_pct: 41.2 },
            { symbol: 'AMD', weight: 18, return_pct: 23.8 },
            { symbol: 'SMCI', weight: 16, return_pct: 29.4 },
            { symbol: 'AVGO', weight: 20, return_pct: 21.8 },
          ],
        }),
        buildEntry(steady, {
          rank: 2,
          position_change: 0,
          total_return: 21.0,
          stocks: [
            { symbol: 'MSFT', weight: 34, return_pct: 29.6 },
            { symbol: 'AAPL', weight: 30, return_pct: 19.7 },
            { symbol: 'GOOGL', weight: 22, return_pct: 14.6 },
            { symbol: 'ORCL', weight: 14, return_pct: -4.8 },
          ],
        }),
        buildEntry(momentum, {
          rank: 3,
          position_change: 0,
          total_return: 20.8,
          stocks: [
            { symbol: 'META', weight: 42, return_pct: 24.2 },
            { symbol: 'GOOGL', weight: 28, return_pct: 15.4 },
            { symbol: 'SNAP', weight: 20, return_pct: 8.2 },
            { symbol: 'PINS', weight: 10, return_pct: -5.2 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.2,
        stocks: [
          { symbol: 'INTC', weight: 24, return_pct: -15.7 },
          { symbol: 'PARA', weight: 20, return_pct: -9.8 },
          { symbol: 'WBD', weight: 21, return_pct: -8.7 },
          { symbol: 'NOK', weight: 20, return_pct: -6.3 },
          { symbol: 'VZ', weight: 15, return_pct: 3.7 },
        ],
      }),
    },
    {
      topThree: [
        buildEntry(nvidia, {
          rank: 1,
          position_change: 0,
          total_return: 21.5,
          stocks: [
            { symbol: 'NVDA', weight: 44, return_pct: 42.1 },
            { symbol: 'AMD', weight: 20, return_pct: 24.6 },
            { symbol: 'SMCI', weight: 18, return_pct: 30.1 },
            { symbol: 'AVGO', weight: 18, return_pct: 22.1 },
          ],
        }),
        buildEntry(momentum, {
          rank: 2,
          position_change: 1,
          total_return: 21.2,
          stocks: [
            { symbol: 'META', weight: 44, return_pct: 27.2 },
            { symbol: 'GOOGL', weight: 26, return_pct: 16.3 },
            { symbol: 'SNAP', weight: 20, return_pct: 11.5 },
            { symbol: 'PINS', weight: 10, return_pct: -2.4 },
          ],
        }),
        buildEntry(steady, {
          rank: 3,
          position_change: -1,
          total_return: 21.1,
          stocks: [
            { symbol: 'MSFT', weight: 33, return_pct: 29.0 },
            { symbol: 'AAPL', weight: 31, return_pct: 19.2 },
            { symbol: 'GOOGL', weight: 21, return_pct: 14.1 },
            { symbol: 'ORCL', weight: 15, return_pct: -6.2 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.0,
        stocks: [
          { symbol: 'INTC', weight: 24, return_pct: -15.2 },
          { symbol: 'PARA', weight: 19, return_pct: -9.4 },
          { symbol: 'WBD', weight: 22, return_pct: -8.3 },
          { symbol: 'NOK', weight: 20, return_pct: -5.9 },
          { symbol: 'VZ', weight: 15, return_pct: 4.3 },
        ],
      }),
    },
    {
      topThree: [
        buildEntry(nvidia, {
          rank: 1,
          position_change: 0,
          total_return: 21.6,
          stocks: [
            { symbol: 'NVDA', weight: 45, return_pct: 41.6 },
            { symbol: 'AMD', weight: 19, return_pct: 24.0 },
            { symbol: 'SMCI', weight: 17, return_pct: 29.3 },
            { symbol: 'AVGO', weight: 19, return_pct: 21.5 },
          ],
        }),
        buildEntry(momentum, {
          rank: 2,
          position_change: 1,
          total_return: 21.4,
          stocks: [
            { symbol: 'META', weight: 43, return_pct: 26.4 },
            { symbol: 'GOOGL', weight: 27, return_pct: 16.0 },
            { symbol: 'SNAP', weight: 20, return_pct: 10.7 },
            { symbol: 'PINS', weight: 10, return_pct: -3.1 },
          ],
        }),
        buildEntry(steady, {
          rank: 3,
          position_change: -1,
          total_return: 21.0,
          stocks: [
            { symbol: 'MSFT', weight: 34, return_pct: 28.7 },
            { symbol: 'AAPL', weight: 30, return_pct: 18.8 },
            { symbol: 'GOOGL', weight: 21, return_pct: 13.9 },
            { symbol: 'ORCL', weight: 15, return_pct: -6.0 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.3,
        stocks: [
          { symbol: 'INTC', weight: 24, return_pct: -15.9 },
          { symbol: 'PARA', weight: 20, return_pct: -10.1 },
          { symbol: 'WBD', weight: 21, return_pct: -8.8 },
          { symbol: 'NOK', weight: 20, return_pct: -6.2 },
          { symbol: 'VZ', weight: 15, return_pct: 3.9 },
        ],
      }),
    },
    {
      topThree: [
        buildEntry(nvidia, {
          rank: 1,
          position_change: 1,
          total_return: 22.0,
          stocks: [
            { symbol: 'NVDA', weight: 45, return_pct: 40.8 },
            { symbol: 'AMD', weight: 19, return_pct: 23.5 },
            { symbol: 'SMCI', weight: 16, return_pct: 28.5 },
            { symbol: 'AVGO', weight: 20, return_pct: 21.1 },
          ],
        }),
        buildEntry(momentum, {
          rank: 2,
          position_change: -1,
          total_return: 21.8,
          stocks: [
            { symbol: 'META', weight: 42, return_pct: 25.7 },
            { symbol: 'GOOGL', weight: 28, return_pct: 15.6 },
            { symbol: 'SNAP', weight: 20, return_pct: 9.6 },
            { symbol: 'PINS', weight: 10, return_pct: -3.8 },
          ],
        }),
        buildEntry(steady, {
          rank: 3,
          position_change: 0,
          total_return: 21.4,
          stocks: [
            { symbol: 'MSFT', weight: 35, return_pct: 29.3 },
            { symbol: 'AAPL', weight: 29, return_pct: 19.4 },
            { symbol: 'GOOGL', weight: 21, return_pct: 14.0 },
            { symbol: 'ORCL', weight: 15, return_pct: -5.2 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.5,
        stocks: [
          { symbol: 'INTC', weight: 25, return_pct: -16.2 },
          { symbol: 'PARA', weight: 20, return_pct: -10.3 },
          { symbol: 'WBD', weight: 20, return_pct: -9.1 },
          { symbol: 'NOK', weight: 20, return_pct: -6.4 },
          { symbol: 'VZ', weight: 15, return_pct: 3.5 },
        ],
      }),
    },
    {
      topThree: [
        buildEntry(momentum, {
          rank: 1,
          position_change: 1,
          total_return: 21.9,
          stocks: [
            { symbol: 'META', weight: 41, return_pct: 26.0 },
            { symbol: 'GOOGL', weight: 29, return_pct: 15.8 },
            { symbol: 'SNAP', weight: 20, return_pct: 10.1 },
            { symbol: 'PINS', weight: 10, return_pct: -3.5 },
          ],
        }),
        buildEntry(nvidia, {
          rank: 2,
          position_change: -1,
          total_return: 21.7,
          stocks: [
            { symbol: 'NVDA', weight: 44, return_pct: 40.5 },
            { symbol: 'AMD', weight: 20, return_pct: 23.2 },
            { symbol: 'SMCI', weight: 17, return_pct: 28.1 },
            { symbol: 'AVGO', weight: 19, return_pct: 20.9 },
          ],
        }),
        buildEntry(steady, {
          rank: 3,
          position_change: 0,
          total_return: 21.2,
          stocks: [
            { symbol: 'MSFT', weight: 34, return_pct: 28.9 },
            { symbol: 'AAPL', weight: 30, return_pct: 18.9 },
            { symbol: 'GOOGL', weight: 21, return_pct: 13.8 },
            { symbol: 'ORCL', weight: 15, return_pct: -5.7 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.4,
        stocks: [
          { symbol: 'INTC', weight: 25, return_pct: -16.0 },
          { symbol: 'PARA', weight: 20, return_pct: -10.1 },
          { symbol: 'WBD', weight: 20, return_pct: -8.9 },
          { symbol: 'NOK', weight: 20, return_pct: -6.2 },
          { symbol: 'VZ', weight: 15, return_pct: 3.6 },
        ],
      }),
    },
    {
      topThree: [
        buildEntry(nvidia, {
          rank: 1,
          position_change: 0,
          total_return: 21.8,
          stocks: [
            { symbol: 'NVDA', weight: 45, return_pct: 40.6 },
            { symbol: 'AMD', weight: 19, return_pct: 23.3 },
            { symbol: 'SMCI', weight: 17, return_pct: 28.2 },
            { symbol: 'AVGO', weight: 19, return_pct: 21.0 },
          ],
        }),
        buildEntry(momentum, {
          rank: 2,
          position_change: -1,
          total_return: 21.5,
          stocks: [
            { symbol: 'META', weight: 42, return_pct: 25.7 },
            { symbol: 'GOOGL', weight: 28, return_pct: 15.6 },
            { symbol: 'SNAP', weight: 20, return_pct: 9.6 },
            { symbol: 'PINS', weight: 10, return_pct: -3.8 },
          ],
        }),
        buildEntry(steady, {
          rank: 3,
          position_change: 0,
          total_return: 21.3,
          stocks: [
            { symbol: 'MSFT', weight: 35, return_pct: 29.0 },
            { symbol: 'AAPL', weight: 29, return_pct: 19.1 },
            { symbol: 'GOOGL', weight: 21, return_pct: 13.9 },
            { symbol: 'ORCL', weight: 15, return_pct: -5.4 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.4,
        stocks: [
          { symbol: 'INTC', weight: 25, return_pct: -16.0 },
          { symbol: 'PARA', weight: 20, return_pct: -10.0 },
          { symbol: 'WBD', weight: 20, return_pct: -8.9 },
          { symbol: 'NOK', weight: 20, return_pct: -6.2 },
          { symbol: 'VZ', weight: 15, return_pct: 3.7 },
        ],
      }),
    },
    {
      topThree: [
        buildEntry(nvidia, {
          rank: 1,
          position_change: 0,
          total_return: 21.6,
          stocks: [
            { symbol: 'NVDA', weight: 45, return_pct: 40.4 },
            { symbol: 'AMD', weight: 20, return_pct: 23.1 },
            { symbol: 'SMCI', weight: 16, return_pct: 28.0 },
            { symbol: 'AVGO', weight: 19, return_pct: 20.7 },
          ],
        }),
        buildEntry(steady, {
          rank: 2,
          position_change: 1,
          total_return: 21.4,
          stocks: [
            { symbol: 'MSFT', weight: 35, return_pct: 29.1 },
            { symbol: 'AAPL', weight: 29, return_pct: 19.2 },
            { symbol: 'GOOGL', weight: 21, return_pct: 14.0 },
            { symbol: 'ORCL', weight: 15, return_pct: -5.1 },
          ],
        }),
        buildEntry(momentum, {
          rank: 3,
          position_change: -1,
          total_return: 21.3,
          stocks: [
            { symbol: 'META', weight: 42, return_pct: 25.6 },
            { symbol: 'GOOGL', weight: 28, return_pct: 15.5 },
            { symbol: 'SNAP', weight: 20, return_pct: 9.4 },
            { symbol: 'PINS', weight: 10, return_pct: -3.9 },
          ],
        }),
      ],
      lastEntry: buildEntry(lastPlaceBase, {
        position_change: 0,
        total_return: -8.5,
        stocks: [
          { symbol: 'INTC', weight: 25, return_pct: -16.2 },
          { symbol: 'PARA', weight: 20, return_pct: -10.3 },
          { symbol: 'WBD', weight: 20, return_pct: -9.1 },
          { symbol: 'NOK', weight: 20, return_pct: -6.4 },
          { symbol: 'VZ', weight: 15, return_pct: 3.5 },
        ],
      }),
    },
  ]
}
